import * as Sentry from '@sentry/browser';
import { Component, type ErrorInfo, type PropsWithChildren, type ReactNode } from 'react';

import { ApiError } from '#configuration/error/ApiError.ts';

import ErrorFallback from './ErrorFallback.tsx';

type ErrorBoundaryProps = {
    fallback?: ReactNode;
};

type ErrorBoundaryState = {
    hasError: boolean;
    error: Error | null;
};

export class ErrorBoundary extends Component<PropsWithChildren<ErrorBoundaryProps>, ErrorBoundaryState> {
    constructor(props: PropsWithChildren<{ fallback: ReactNode }>) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(_error: unknown) {
        return { hasError: true, error: _error };
    }

    componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
        console.error('[ErrorBoundary]', error, errorInfo);
        // Ignore ApiErrors to reduce noise in Sentry, as they are logged in datadog
        if (error instanceof ApiError) {
            return;
        }
        Sentry.captureException(error, {
            data: errorInfo,
        });
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            if (this.state.error?.message) {
                return <ErrorFallback errorMessage={this.state.error?.message} />;
            }
            return <ErrorFallback errorMessage={'An Error Occurred'} />;
        }
        return this.props.children;
    }
}
